import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProjects, Project } from '../utils/projectStorage';

const Home: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const loadedProjects = getProjects();
    setProjects(loadedProjects);
  }, []);

  // Otomatik kaydırma
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % Math.ceil(projects.length / 3));
    }, 5000); // 5 saniyede bir kaydır

    return () => clearInterval(interval);
  }, [projects.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % Math.ceil(projects.length / 3));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + Math.ceil(projects.length / 3)) % Math.ceil(projects.length / 3));
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content fade-in-up">
          <p className="hero-subtitle">Mimar</p>
          <h1 className="display-1 hero-title">Selenay Oğuz</h1>
          <p className="body-large hero-description">
            Estetik ve işlevselliği bir araya getiren, yenilikçi mimari tasarımlar
          </p>
          <div className="flex-center gap-md">
            <Link to="/projeler" className="btn btn-primary">
              Projelerimi Keşfet
            </Link>
            <a href="#iletisim" className="btn btn-outline">
              İletişime Geç
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section" id="hakkimda">
        <div className="container">
          <div className="slide-in-left" style={{maxWidth: '800px', margin: '0 auto'}}>
            <div className="accent-line"></div>
            <h2 className="heading-1 mb-md">Hakkımda</h2>
            <p className="body-large text-secondary mb-md">
              Estetik ve işlevselliği tasarımda değer veren bir mimarım. Tarihi mimarlık ve 
              sürdürülebilir çözümler konularında özel ilgim var.
            </p>
            <p className="body-base text-secondary mb-lg">
              Takım çalışmasını seviyorum ve yenilikçi, ilham verici projelere katkıda 
              bulunmaktan gurur duyuyorum. Her proje için benzersiz ve fonksiyonel çözümler 
              üretmeyi hedefliyorum.
            </p>
            <Link to="/hizmetler" className="btn btn-outline">
              Hizmetlerim
            </Link>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="section-sm" style={{background: 'var(--color-bg-card)'}}>
        <div className="container">
          <div className="text-center mb-xl">
            <div className="accent-line" style={{margin: '0 auto var(--spacing-sm)'}}></div>
            <h2 className="heading-1">Uzmanlık Alanları</h2>
          </div>
          <div className="grid grid-4">
            {[
              { icon: '🏠', title: 'Konut Tasarımı', desc: 'Modern ve fonksiyonel yaşam alanları' },
              { icon: '🏢', title: 'Ticari Mekanlar', desc: 'İşlevsel ofis ve mağaza tasarımları' },
              { icon: '📐', title: 'Teknik Çizim', desc: 'Detaylı mimari projeler ve planlar' },
              { icon: '🎨', title: '3D Modelleme', desc: 'Fotorealistik görselleştirmeler' }
            ].map((item, index) => (
              <div key={index} className={`card fade-in-up animate-delay-${index + 1}`} style={{padding: 'var(--spacing-md)', textAlign: 'center'}}>
                <div style={{fontSize: '3rem', marginBottom: 'var(--spacing-sm)'}}>{item.icon}</div>
                <h3 className="heading-3 mb-sm">{item.title}</h3>
                <p className="body-small text-secondary">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="section-sm">
        <div className="container">
          <div className="text-center mb-md">
            <div className="accent-line" style={{margin: '0 auto var(--spacing-sm)'}}></div>
            <h2 className="heading-2">Öne Çıkan Projeler</h2>
          </div>
          
          {projects.length > 0 && (
            <div style={{position: 'relative'}}>
              {/* Carousel Container */}
              <div style={{overflow: 'hidden', position: 'relative'}}>
                <div 
                  style={{
                    display: 'flex',
                    transition: 'transform 0.5s ease-in-out',
                    transform: `translateX(-${currentSlide * 100}%)`
                  }}
                >
                  {Array.from({ length: Math.ceil(projects.length / 3) }).map((_, slideIndex) => (
                    <div 
                      key={slideIndex}
                      style={{
                        minWidth: '100%',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: 'var(--spacing-md)'
                      }}
                    >
                      {projects.slice(slideIndex * 3, slideIndex * 3 + 3).map((project, index) => (
                        <Link 
                          key={project.id}
                          to={`/proje/${project.id}`}
                          style={{textDecoration: 'none', color: 'inherit'}}
                        >
                          <div className="card" style={{
                            position: 'relative',
                            overflow: 'hidden',
                            height: '280px',
                            cursor: 'pointer',
                            transition: 'transform 0.3s ease'
                          }}>
                            {project.image ? (
                              <img 
                                src={project.image} 
                                alt={project.title}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            ) : (
                              <div className="img-placeholder" style={{height: '100%'}}>
                                <span style={{fontSize: '3rem'}}>{project.icon}</span>
                              </div>
                            )}
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
                              padding: 'var(--spacing-md)',
                              color: 'white'
                            }}>
                              <p className="body-small" style={{opacity: 0.8, marginBottom: '0.25rem'}}>
                                {project.category} • {project.year}
                              </p>
                              <h3 className="heading-4" style={{color: 'white', marginBottom: '0.25rem'}}>
                                {project.title.length > 40 ? project.title.substring(0, 40) + '...' : project.title}
                              </h3>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation Buttons */}
              {Math.ceil(projects.length / 3) > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    style={{
                      position: 'absolute',
                      left: '-15px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'var(--color-accent)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '35px',
                      height: '35px',
                      cursor: 'pointer',
                      fontSize: '1.2rem',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      zIndex: 10
                    }}
                    aria-label="Önceki"
                  >
                    ←
                  </button>
                  <button
                    onClick={nextSlide}
                    style={{
                      position: 'absolute',
                      right: '-15px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'var(--color-accent)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '35px',
                      height: '35px',
                      cursor: 'pointer',
                      fontSize: '1.2rem',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      zIndex: 10
                    }}
                    aria-label="Sonraki"
                  >
                    →
                  </button>
                </>
              )}

              {/* Dots Indicator */}
              {Math.ceil(projects.length / 3) > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '8px',
                  marginTop: 'var(--spacing-md)'
                }}>
                  {Array.from({ length: Math.ceil(projects.length / 3) }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      style={{
                        width: currentSlide === index ? '24px' : '8px',
                        height: '8px',
                        borderRadius: '4px',
                        border: 'none',
                        background: currentSlide === index ? 'var(--color-accent)' : '#ccc',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      aria-label={`Slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Experience Section */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-xl">
            <div className="accent-line" style={{margin: '0 auto var(--spacing-sm)'}}></div>
            <h2 className="heading-1">Deneyim</h2>
          </div>
          <div className="grid grid-3">
            <div className="card scale-in animate-delay-1" style={{padding: 'var(--spacing-md)'}}>
              <p className="body-small accent-text mb-sm">2024</p>
              <h3 className="heading-3 mb-sm">Gazi Oflaz Architecture Office</h3>
              <p className="body-small text-muted mb-sm">Mimar Stajyeri</p>
              <p className="body-small text-secondary">
                Site analizi, malzeme seçimi, 3D modelleme ve rendering. 
                Proje geliştirme ve teknik dokümantasyon.
              </p>
            </div>
            <div className="card scale-in animate-delay-2" style={{padding: 'var(--spacing-md)'}}>
              <p className="body-small accent-text mb-sm">2023</p>
              <h3 className="heading-3 mb-sm">MMSS Savunma Bilişim</h3>
              <p className="body-small text-muted mb-sm">Grafik Tasarımcı</p>
              <p className="body-small text-secondary">
                Logo tasarımı, mobil uygulama tasarımı ve web sitesi tasarımı.
              </p>
            </div>
            <div className="card scale-in animate-delay-3" style={{padding: 'var(--spacing-md)'}}>
              <p className="body-small accent-text mb-sm">2021</p>
              <h3 className="heading-3 mb-sm">ROTA+ Architecture Office</h3>
              <p className="body-small text-muted mb-sm">Mimar Stajyeri</p>
              <p className="body-small text-secondary">
                Konut tasarımı, 3D modelleme, yüksek kaliteli renderlar ve teknik çizimler.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Education Section */}
      <section className="section-sm" style={{background: 'var(--color-bg-card)'}}>
        <div className="container">
          <div className="text-center mb-xl">
            <div className="accent-line" style={{margin: '0 auto var(--spacing-sm)'}}></div>
            <h2 className="heading-1">Eğitim</h2>
          </div>
          <div className="grid grid-2">
            <div className="card" style={{padding: 'var(--spacing-md)'}}>
              <p className="body-small accent-text mb-sm">2018 - 2022</p>
              <h3 className="heading-3 mb-sm">Süleyman Demirel Üniversitesi</h3>
              <p className="body-base text-secondary">Mimarlık Bölümü (Lisans)</p>
            </div>
            <div className="card" style={{padding: 'var(--spacing-md)'}}>
              <p className="body-small accent-text mb-sm">2022 - 2024</p>
              <h3 className="heading-3 mb-sm">Toros Üniversitesi</h3>
              <p className="body-base text-secondary">Mimarlık Bölümü (Lisans Tamamlama)</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section" id="iletisim">
        <div className="container">
          <div className="text-center mb-xl">
            <div className="accent-line" style={{margin: '0 auto var(--spacing-sm)'}}></div>
            <h2 className="heading-1">İletişim</h2>
            <p className="body-large text-secondary mt-md">Projeniz için benimle iletişime geçin</p>
          </div>
          <div className="grid grid-3">
            <div className="card fade-in-up animate-delay-1" style={{padding: 'var(--spacing-lg)', textAlign: 'center'}}>
              <div style={{fontSize: '3rem', marginBottom: 'var(--spacing-md)', color: 'var(--color-accent)'}}>📧</div>
              <p className="body-small text-muted mb-sm">Email</p>
              <a href="mailto:selenay.keskin.33@gmail.com" className="body-base accent-text" style={{textDecoration: 'none', wordBreak: 'break-word'}}>
                selenay.keskin.33@gmail.com
              </a>
            </div>
            <div className="card fade-in-up animate-delay-2" style={{padding: 'var(--spacing-lg)', textAlign: 'center'}}>
              <div style={{fontSize: '3rem', marginBottom: 'var(--spacing-md)', color: 'var(--color-accent)'}}>📱</div>
              <p className="body-small text-muted mb-sm">Telefon</p>
              <a href="tel:+905366613488" className="body-base accent-text" style={{textDecoration: 'none'}}>
                +90 (536) 661 34 88
              </a>
            </div>
            <div className="card fade-in-up animate-delay-3" style={{padding: 'var(--spacing-lg)', textAlign: 'center'}}>
              <div style={{fontSize: '3rem', marginBottom: 'var(--spacing-md)', color: 'var(--color-accent)'}}>💼</div>
              <p className="body-small text-muted mb-sm">LinkedIn</p>
              <a href="https://linkedin.com/in/selenaykeskin" target="_blank" rel="noopener noreferrer" className="body-base accent-text" style={{textDecoration: 'none'}}>
                linkedin.com/in/selenaykeskin
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
